import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Kpis } from '../../components/charts';
import { CLAIM_CHIP, CLAIM_LABEL } from './Claims';

/* ============================================================
   Satu klaim, baris per baris.

   Dua kolom angka, dan mereka tidak pernah menjadi satu. Kolom
   terlapor tidak bisa disunting setelah klaim diajukan; kolom
   terverifikasi hanya bisa diisi saat klaim sedang diverifikasi.
   Selisihnya dihitung, tidak diketik.
   ============================================================ */

type Row = {
  lineId: string;
  activityId: string;
  activityCode: string;
  name: string;
  wbs: string | null;
  weight: number;
  reportedPct: number;
  reportedQty: number | null;
  unit: string | null;
  blockedByDeliverable: boolean;
  verifiedPct: number | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  method: string | null;
  evidenceCount: number;
  gap: number | null;
};

type Totals = {
  reported: number;
  verified: number;
  gap: number;
  lines: number;
  verifiedLines: number;
  unverifiedLines: number;
  blockedLines: number;
  equalFallback: boolean;
};

type Detail = {
  claim: {
    id: string;
    state: string;
    reference: string | null;
    note: string | null;
    period: { periodNo: number; start: string; end: string; status: string };
    submittedBy: { name: string } | null;
  };
  rows: Row[];
  totals: Totals;
};

/** Aksi yang boleh dilakukan dari tiap status — cermin tabel di inti. */
const ACTIONS: Record<string, { action: string; label: string; primary?: boolean }[]> = {
  DRAFT: [{ action: 'SUBMIT', label: 'Ajukan', primary: true }],
  SUBMITTED: [
    { action: 'ASSIGN', label: 'Mulai verifikasi', primary: true },
    { action: 'RETURN', label: 'Kembalikan' },
  ],
  UNDER_VERIFICATION: [
    { action: 'COMPLETE_VERIFICATION', label: 'Verifikasi selesai', primary: true },
    { action: 'RETURN', label: 'Kembalikan' },
  ],
  RETURNED: [{ action: 'RESUBMIT', label: 'Ajukan ulang', primary: true }],
  VERIFIED: [
    { action: 'CERTIFY', label: 'Sertifikasi', primary: true },
    { action: 'DISPUTE', label: 'Bantah' },
  ],
  DISPUTED: [{ action: 'REOPEN', label: 'Buka verifikasi lagi', primary: true }],
  CERTIFIED: [],
};

export function ClaimDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setData(await api.get<Detail>(`/progress/claims/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the claim.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const { claim, rows, totals } = data;
  const editable = claim.state === 'DRAFT' || claim.state === 'RETURNED';
  const verifying = claim.state === 'UNDER_VERIFICATION' || claim.state === 'DISPUTED';

  async function saveReported() {
    const payload = Object.entries(edits)
      .map(([activityId, v]) => ({ activityId, reportedPct: Number(v) }))
      .filter((r) => Number.isFinite(r.reportedPct));
    if (payload.length === 0) return;

    setBusy(true);
    try {
      await api.put(`/progress/claims/${id}/reported`, { rows: payload });
      setEdits({});
      setNotice(`${payload.length} baris tersimpan.`);
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal menyimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function verify(lineId: string, value: string) {
    const pct = Number(value);
    if (!Number.isFinite(pct)) return;
    setBusy(true);
    try {
      await api.post(`/progress/lines/${lineId}/verify`, { verifiedPct: pct, method: 'WALK' });
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal mencatat verifikasi.');
    } finally {
      setBusy(false);
    }
  }

  async function act(action: string) {
    const needsNote = action === 'RETURN' || action === 'DISPUTE';
    const note = needsNote ? window.prompt('Alasannya — wajib, dan ikut tersimpan:') : undefined;
    if (needsNote && !note?.trim()) return;

    setBusy(true);
    try {
      const r = await api.post<{ from: string; to: string }>(`/progress/claims/${id}/actions`, {
        action,
        note,
      });
      setNotice(`${CLAIM_LABEL[r.from] ?? r.from} → ${CLAIM_LABEL[r.to] ?? r.to}`);
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Klaim P{claim.period.periodNo}</h1>
          <p>
            {fmtDate(claim.period.start)} – {fmtDate(claim.period.end)}
            {claim.submittedBy && ` · diajukan ${claim.submittedBy.name}`}
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${CLAIM_CHIP[claim.state] ?? ''}`}>
            {CLAIM_LABEL[claim.state] ?? claim.state}
          </span>
          {(ACTIONS[claim.state] ?? []).map((a) => (
            <button
              key={a.action}
              className={a.primary ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              disabled={busy}
              onClick={() => act(a.action)}
            >
              {a.label}
            </button>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/claims')}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <Kpis
        items={[
          { label: 'Dilaporkan', value: `${totals.reported.toFixed(1)}%` },
          { label: 'Terverifikasi', value: `${totals.verified.toFixed(1)}%` },
          {
            label: 'Selisih',
            value: `${totals.gap > 0 ? '−' : totals.gap < 0 ? '+' : ''}${Math.abs(totals.gap).toFixed(1)} pts`,
            note: totals.gap > 5 ? 'di atas ambang, perlu dijelaskan' : undefined,
            tone: totals.gap > 5 ? 'crit' : totals.gap > 0 ? 'warn' : 'good',
          },
          {
            label: 'Baris diverifikasi',
            value: `${totals.verifiedLines} / ${totals.lines}`,
            note: totals.blockedLines > 0 ? `${totals.blockedLines} terhalang deliverable` : undefined,
            tone: totals.blockedLines > 0 ? 'warn' : undefined,
          },
        ]}
      />

      {totals.equalFallback && (
        <div className="banner">
          Bobot aktivitas belum ditetapkan, jadi angka di atas memakai pembagian rata. Tetapkan bobot
          sebelum angka ini dipakai untuk sertifikasi.
        </div>
      )}

      {totals.unverifiedLines > 0 && verifying && (
        <p className="hint" style={{ marginBottom: 14 }}>
          {totals.unverifiedLines} baris belum punya angka. Verifikasi tidak bisa dinyatakan selesai
          sampai semuanya terisi — nol adalah angka, kosong bukan.
        </p>
      )}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Aktivitas</th>
              <th>WBS</th>
              <th>Bobot</th>
              <th>Dilaporkan</th>
              <th>Terverifikasi</th>
              <th>Selisih</th>
              <th>Oleh</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lineId} className="no-click">
                <td className="num">
                  {r.activityCode}
                  <div className="hint">{r.name}</div>
                  {r.blockedByDeliverable && (
                    <span className="chip warn" title="Deliverable belum IFC">
                      Terhalang gambar
                    </span>
                  )}
                </td>
                <td>{r.wbs ?? '—'}</td>
                <td>{r.weight || '—'}</td>
                <td>
                  {editable ? (
                    <input
                      className="input"
                      style={{ width: 90 }}
                      type="number"
                      min={0}
                      max={100}
                      step="0.1"
                      defaultValue={r.reportedPct}
                      onChange={(e) => setEdits((s) => ({ ...s, [r.activityId]: e.target.value }))}
                    />
                  ) : (
                    `${r.reportedPct.toFixed(1)}%`
                  )}
                </td>
                <td>
                  {verifying ? (
                    <input
                      className="input"
                      style={{ width: 90 }}
                      type="number"
                      min={0}
                      max={100}
                      step="0.1"
                      defaultValue={r.verifiedPct ?? ''}
                      placeholder="—"
                      onBlur={(e) => {
                        if (e.target.value !== '' && Number(e.target.value) !== r.verifiedPct) {
                          void verify(r.lineId, e.target.value);
                        }
                      }}
                    />
                  ) : r.verifiedPct == null ? (
                    <span className="hint">belum</span>
                  ) : (
                    `${r.verifiedPct.toFixed(1)}%`
                  )}
                </td>
                <td className={r.gap == null ? '' : r.gap > 5 ? 'neg' : r.gap < 0 ? 'pos' : ''}>
                  {r.gap == null ? '—' : `${r.gap > 0 ? '−' : r.gap < 0 ? '+' : ''}${Math.abs(r.gap).toFixed(1)}`}
                </td>
                <td className="hint">
                  {r.verifiedBy ?? '—'}
                  {r.evidenceCount > 0 && ` · ${r.evidenceCount} bukti`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editable && Object.keys(edits).length > 0 && (
        <div className="dialog-actions" style={{ marginTop: 16 }}>
          <button className="btn btn-primary" disabled={busy} onClick={saveReported}>
            Simpan {Object.keys(edits).length} baris
          </button>
        </div>
      )}

      {claim.note && (
        <section className="panel" style={{ marginTop: 20 }}>
          <header className="panel-head">
            <h3>Catatan</h3>
          </header>
          <pre className="lcomment" style={{ whiteSpace: 'pre-wrap' }}>
            {claim.note}
          </pre>
        </section>
      )}
    </>
  );
}
